import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCurrentUser } from '../../hooks/useCurrentUser';
import * as api from '../../services/api';
import {
  Criteria,
  CriteriaPurpose,
  CriterionTranscriptionAssignment,
  Essay,
  EssayPurpose,
  Purpose,
  Transcription,
  User,
} from '../../types';
import Loading from '../Loading';
import AssignmentCreatePanel, { type AssignmentCreatePanelHandle } from './AssignmentCreatePanel';
import AssignmentsBoard, { type TranscriptionDescription } from './AssignmentsBoard';
import AssignToOthersDialog from './AssignToOthersDialog';
import { type AssignmentPair, uniqueAssignmentPairs } from './assignToOthers';
import { isOpenAssignment } from './assignmentProgress';

const AssignmentsManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [essays, setEssays] = useState<Essay[]>([]);
  const [assignments, setAssignments] = useState<CriterionTranscriptionAssignment[]>([]);
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [essayPurposes, setEssayPurposes] = useState<EssayPurpose[]>([]);
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [criteriaPurposes, setCriteriaPurposes] = useState<CriteriaPurpose[]>([]);
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const createPanelRef = useRef<AssignmentCreatePanelHandle>(null);

  const [isLoadingPageData, setIsLoadingPageData] = useState(true);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] =
    useState<CriterionTranscriptionAssignment | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [assignToOthersPairs, setAssignToOthersPairs] = useState<AssignmentPair[] | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'success', message: '' });

  const currentUserId = currentUser?.id;

  useEffect(() => {
    if (!currentUserId) {
      setIsLoadingPageData(false);
      return;
    }

    setIsLoadingPageData(true);

    const fetchData = async () => {
      try {
        const [
          usersData,
          transcriptionCriterionAssignmentsData,
          criteriaData,
          transcriptionsData,
          essaysData,
          essayPurposesData,
          purposesData,
          criteriaPurposesData,
        ] = await Promise.all([
          api.fetchUsers(),
          api.fetchCriterionTranscriptionAssignments(),
          api.fetchCriteria(),
          api.fetchTranscriptions(),
          api.fetchEssays(),
          api.fetchEssayPurposes(),
          api.fetchPurposes(),
          api.fetchCriteriaPurposes(),
        ]);

        setUsers(usersData);
        setAssignments(transcriptionCriterionAssignmentsData);
        setCriteria(criteriaData);
        setTranscriptions(transcriptionsData);
        setEssays(essaysData);
        setEssayPurposes(essayPurposesData);
        setPurposes(purposesData);
        setCriteriaPurposes(criteriaPurposesData);
      } catch (error) {
        console.error('Error fetching assignment data:', error);
      } finally {
        setIsLoadingPageData(false);
      }
    };

    fetchData();
  }, [currentUserId]);

  const getUserName = useCallback(
    (userId: string) => {
      const user = users.find(item => item.id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown Labeller';
    },
    [users]
  );

  const getCriteriaName = useCallback(
    (criteriaId: string) => {
      const criterion = criteria.find(item => item.id === criteriaId);
      return criterion ? criterion.name : 'Unknown';
    },
    [criteria]
  );

  const transcriptionById = useMemo(
    () => new Map(transcriptions.map(item => [item.id, item])),
    [transcriptions]
  );
  const essayById = useMemo(() => new Map(essays.map(item => [item.id, item])), [essays]);
  const purposeNamesByEssay = useMemo(() => {
    const purposeName = new Map(purposes.map(item => [item.id, item.name]));
    const map = new Map<string, string[]>();
    essayPurposes.forEach(row => {
      const name = purposeName.get(row.purposeId);
      if (!name) return;
      const list = map.get(row.essayId);
      if (list) list.push(name);
      else map.set(row.essayId, [name]);
    });
    return map;
  }, [essayPurposes, purposes]);

  const describeTranscription = useCallback(
    (transcriptionId: string): TranscriptionDescription => {
      const transcription = transcriptionById.get(transcriptionId);
      if (!transcription) return { primary: 'Unknown transcription', secondary: '' };
      const essay = essayById.get(transcription.essayId);
      if (!essay) return { primary: transcription.versionName, secondary: '' };
      const purposeNames = purposeNamesByEssay.get(essay.id)?.join(', ') ?? 'N/A';
      return {
        primary: `Paper ${essay.paper_number} · ${transcription.versionName}`,
        secondary: `${purposeNames} · ${essay.learn_cycle_batch}`,
      };
    },
    [essayById, purposeNamesByEssay, transcriptionById]
  );

  const handleAssignmentsCreated = (created: CriterionTranscriptionAssignment[]) => {
    if (created.length === 0) return;
    setAssignments(prev => [...prev, ...created]);
  };

  const handleAssignedToOthers = ({
    created,
    createdCount,
    userCount,
  }: {
    created: CriterionTranscriptionAssignment[];
    createdCount: number;
    userCount: number;
  }) => {
    handleAssignmentsCreated(created);
    setAssignToOthersPairs(null);
    setSnackbar({
      open: true,
      severity: 'success',
      message:
        createdCount === 1
          ? 'Created 1 assignment.'
          : `Created ${createdCount} assignments across ${userCount} people.`,
    });
  };

  const handleRequestDelete = (ids: string[], origin: 'row' | 'bulk' = 'bulk') => {
    if (ids.length === 0) return;
    if (origin === 'row' && ids.length === 1) {
      const assignment = assignments.find(item => item.id === ids[0]);
      if (!assignment || !isOpenAssignment(assignment)) return;
      setAssignmentToDelete(assignment);
      setPendingDeleteIds([]);
      setShowDeleteConfirmDialog(true);
      return;
    }
    setAssignmentToDelete(null);
    setPendingDeleteIds(ids);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteHandler = async () => {
    const ids =
      pendingDeleteIds.length > 0
        ? pendingDeleteIds
        : assignmentToDelete && isOpenAssignment(assignmentToDelete)
          ? [assignmentToDelete.id]
          : [];
    if (ids.length === 0) return;

    setIsDeleting(true);
    try {
      const result = await api.bulkDeleteAssignments(ids);
      const requested = new Set(ids);
      setAssignments(prev =>
        prev.filter(assignment => !(requested.has(assignment.id) && isOpenAssignment(assignment)))
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          result.skippedStartedCount > 0
            ? `Deleted ${result.deletedCount} open assignment${result.deletedCount === 1 ? '' : 's'}; skipped ${result.skippedStartedCount} started.`
            : `Deleted ${result.deletedCount} assignment${result.deletedCount === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      console.error('Error deleting assignments:', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to delete assignments.',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmDialog(false);
      setAssignmentToDelete(null);
      setPendingDeleteIds([]);
    }
  };

  const handleCloseDeleteConfirmDialog = () => {
    if (isDeleting) return;
    setShowDeleteConfirmDialog(false);
    setAssignmentToDelete(null);
    setPendingDeleteIds([]);
  };

  if (isLoadingUser) return <Loading />;
  if (isLoadingPageData && assignments.length === 0) return <Loading />;

  const assignmentToDeleteTranscription = assignmentToDelete
    ? transcriptionById.get(assignmentToDelete.transcriptionId)
    : undefined;

  return (
    <>
      <AssignmentsBoard
        assignments={assignments}
        getUserName={getUserName}
        getCriteriaName={getCriteriaName}
        describeTranscription={describeTranscription}
        onRequestDelete={handleRequestDelete}
        onAssignFromSelection={selectedAssignments => {
          const pairs = uniqueAssignmentPairs(selectedAssignments);
          if (pairs.length === 0) return;
          setAssignToOthersPairs(pairs);
        }}
        createPanel={({ groupBy, groupId }) => (
          <AssignmentCreatePanel
            ref={createPanelRef}
            users={users}
            criteria={criteria}
            transcriptions={transcriptions}
            essays={essays}
            essayPurposes={essayPurposes}
            purposes={purposes}
            criteriaPurposes={criteriaPurposes}
            assignments={assignments}
            initialCriteriaIds={groupBy === 'criterion' && groupId ? [groupId] : []}
            onCreated={handleAssignmentsCreated}
          />
        )}
      />

      {assignToOthersPairs && (
        <AssignToOthersDialog
          open
          pairs={assignToOthersPairs}
          users={users}
          onClose={() => setAssignToOthersPairs(null)}
          onAssigned={handleAssignedToOthers}
        />
      )}

      <Dialog open={showDeleteConfirmDialog} onClose={handleCloseDeleteConfirmDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          {assignmentToDelete ? (
            <DialogContentText>
              Are you sure you want to delete the assignment for{' '}
              <strong>{getUserName(assignmentToDelete.userId)}</strong> on criterion{' '}
              <strong>{getCriteriaName(assignmentToDelete.criteriaId)}</strong> for transcription{' '}
              <strong>
                {assignmentToDeleteTranscription
                  ? `${describeTranscription(assignmentToDelete.transcriptionId).primary} · ${describeTranscription(assignmentToDelete.transcriptionId).secondary}`
                  : assignmentToDelete.transcriptionId}
              </strong>
              ? This action cannot be undone.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Delete {pendingDeleteIds.length} open assignment
              {pendingDeleteIds.length === 1 ? '' : 's'}? Started assignments are never deleted.
              This action cannot be undone.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmDialog} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteHandler} color="secondary" autoFocus disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(current => ({ ...current, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(current => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignmentsManagement;
